import { Typography } from "@mui/material";
import { ReactElement, useContext } from "react";
import { useNavigate } from "react-router-dom";

import {
  TenDlcRegistrationEvent,
  TenDlcRegistrationEventType,
} from "~frontend/__generated__/graphql";
import CollapsibleDrawer from "~frontend/components/CollapsibleDrawer";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import Table from "~frontend/components/Table";
import { palette } from "~frontend/theme";

import { TabNavigationContext } from "./CampaignsTab";

type EventLogProps = {
  events: TenDlcRegistrationEvent[];
  title: string;
};

const getEventTypeLabel = (eventType: TenDlcRegistrationEventType): string => {
  switch (eventType) {
    case "SUBMITTED_TO_TCR":
      return "Submitted to TCR";
    case "SUBMISSION_TO_TCR_FAILED":
      return "TCR Submission Failed";
    case "BRAND_IDENTITY_STATUS_UPDATE":
      return "Brand Identity Status Updated";
    case "BRAND_IMPORTED_FROM_TCR":
      return "Brand Imported from TCR";
    case "EDITED":
      return "Edited";
    case "EXPEDITE_REQUESTED":
      return "Expedite Requested";
    case "EXPEDITE_PAID":
      return "Expedite Paid";
    case "SUBMITTED_CAMPAIGN_TO_TCR":
      return "Submitted Campaign to TCR";
    case "CAMPAIGN_SHARE_REQUESTED":
      return "Campaign Share Requested";
    case "CAMPAIGN_SHARE_REJECTED":
      return "Campaign Share Rejected";
    case "CAMPAIGN_SHARE_ACCEPTED":
      return "Campaign Share Accepted";
    case "CAMPAIGN_DCA_COMPLETED":
      return "Campaign DCA Completed";
    case "CAMPAIGN_COPIED":
      return "Copied";
    default:
      return eventType.replace(/_/g, " ");
  }
};

const EventLogItem = ({
  event,
}: {
  event: TenDlcRegistrationEvent & { data?: Record<string, any> };
}): ReactElement => {
  const eventTypeLabel = getEventTypeLabel(event.type);
  let eventTypeLabelDetail = "";

  if (event.type === "CAMPAIGN_COPIED") {
    if (event.data?.copiedToCampaignId) {
      eventTypeLabelDetail = " to 10DLC Campaign";
    } else if (event.data?.copiedToTollFreeId) {
      eventTypeLabelDetail = " to Toll-Free Campaign";
    }
  }

  const navigateToCampaign = useContext(TabNavigationContext);
  const navigate = useNavigate();

  const dataDetails: { label: string; content: string | ReactElement }[] = [];

  const eventData = event.data || {};

  if (eventData.submittedByStaffEmail) {
    dataDetails.push({
      label: "Submitted by",
      content: eventData.submittedByStaffEmail,
    });
  }

  if (eventData.copiedByStaffEmail) {
    dataDetails.push({
      label: "Copied by",
      content: eventData.copiedByStaffEmail,
    });
  }

  if (eventData.copiedToCampaignId) {
    const regId = eventData.copiedToCampaignId;
    dataDetails.push({
      label: "To",
      content: (
        <LinkButton onClick={() => navigateToCampaign(regId)}>
          {regId}
        </LinkButton>
      ),
    });
  }

  if (eventData.copiedToTollFreeId) {
    const regId = eventData.copiedToTollFreeId;
    dataDetails.push({
      label: "To",
      content: (
        <LinkButton
          onClick={() =>
            navigate(`/staff/compliance-registrations/toll-free/${regId}`)
          }
        >
          {regId}
        </LinkButton>
      ),
    });
  }

  if (eventData.error) {
    dataDetails.push({
      label: "Error",
      content: eventData.error,
    });
  }

  if (eventData.description) {
    dataDetails.push({
      label: "Description",
      content: eventData.description,
    });
  }

  if (eventData.payload) {
    const { __typename, version: _version, ...payload } = eventData.payload;
    dataDetails.push({
      label: "Payload",
      content: <pre>{JSON.stringify(payload, null, 2)}</pre>,
    });
  }

  if (eventData.brandIdentityStatus) {
    dataDetails.push({
      label: "Brand Identity Status",
      content: eventData.brandIdentityStatus,
    });
  }

  return (
    <FlexBox sx={{ flexDirection: "column", width: "100%" }}>
      <Typography variant="body1Bold" sx={{ mb: 1 }}>
        {`${eventTypeLabel}${eventTypeLabelDetail}`}
      </Typography>
      {dataDetails.map(({ label, content }) => (
        <Typography key={label} variant="body2" sx={{ mb: 0.5 }}>
          <strong>{label}:</strong> {content}
        </Typography>
      ))}
    </FlexBox>
  );
};

export const EventLog = ({ events, title }: EventLogProps): ReactElement => {
  if (!events || events.length === 0) {
    return (
      <Typography variant="body1Bold" sx={{ color: palette.navy }}>
        No events logged yet.
      </Typography>
    );
  }

  return (
    <CollapsibleDrawer
      title={
        <Typography variant="body1Bold" sx={{ color: palette.navy }}>
          {title} ({events.length})
        </Typography>
      }
      startOpen={false}
      sxHeader={{ border: 0 }}
    >
      <Table
        rows={events}
        columns={[
          {
            key: "createdAt",
            title: "Time",
            renderValue: event => new Date(event.createdAt).toLocaleString(),
          },
          {
            key: "message",
            title: "Event",
            align: "left",
            renderValue: event => <EventLogItem event={event} />,
          },
        ]}
        noFooter
        emptyState="No events"
      />
    </CollapsibleDrawer>
  );
};
